import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { CORE_CONFIG_TOKEN } from '@ndk/ng-core/component/core.config';
import { NDDTranslationService } from '@ndk/ng-translation';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { Subject } from 'rxjs/Subject';

import { ICoreConfigInternal } from '@core/core.config.model';
import { LoggerService } from '@core/logger/logger.service';

import { ISettingsIntegration360AccessKeysModel, ISettingsIntegration360BehaviorCommand, ISettingsIntegration360BehaviorModel, ISettingsIntegrationPrintJobsAccessKeysModel, ISettingsIntegrationPrintJobsBehaviorCommand, ISettingsIntegrationPrintJobsBehaviorModel } from './models/settings-integrations.model';

@Injectable({
    providedIn: 'root',
})
export class SettingsIntegrationsService {
    private apiUrl: string;
    private refreshShowIntegration360$: Subject<ISettingsIntegration360BehaviorModel> = new Subject<ISettingsIntegration360BehaviorModel>();
    private refreshShowIntegrationPrintJobs$: Subject<ISettingsIntegrationPrintJobsBehaviorModel> =
        new Subject<ISettingsIntegrationPrintJobsBehaviorModel>();

    constructor(
        @Inject(CORE_CONFIG_TOKEN) config: ICoreConfigInternal,
        private http: HttpClient,
        private loggerService: LoggerService,
        private translation: NDDTranslationService,
    ) {
        this.apiUrl = `${ config.apiEndpoint }api/settings-integrations`;
    }

    public get360Behavior(): Observable<ISettingsIntegration360BehaviorModel> {
        return this.http.get<ISettingsIntegration360BehaviorModel>(`${ this.apiUrl }/360-behavior`);
    }

    public update360Activation(command: ISettingsIntegration360BehaviorCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/360-behavior`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360SettingsSuccess')])));
    }

    public get360AccessKeys(): Observable<ISettingsIntegration360AccessKeysModel> {
        return this.http.get<ISettingsIntegration360AccessKeysModel>(`${ this.apiUrl }/360-access-keys`);
    }

    public request360AccessKeys(): Observable<ISettingsIntegration360AccessKeysModel> {
        return this.http.get<ISettingsIntegration360AccessKeysModel>(`${ this.apiUrl }/360-access-keys/request`);
    }

    public revoke360AccessKeys(): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/360-access-keys/revoke`);
    }

    public checkForIntegration360SettingsChanges(): Observable<ISettingsIntegration360BehaviorModel> {
        return this.refreshShowIntegration360$.asObservable();
    }

    public refreshIntegration360Settings(value: boolean): void {
        this.refreshShowIntegration360$.next({ enabled: value });
    }

    public getPrintJobsBehavior(): Observable<ISettingsIntegrationPrintJobsBehaviorModel> {
        return this.http.get<ISettingsIntegrationPrintJobsBehaviorModel>(`${ this.apiUrl }/print-jobs-behavior`);
    }

    public updatePrintJobsActivation(command: ISettingsIntegrationPrintJobsBehaviorCommand): Observable<boolean> {
        return this.http.post<boolean>(`${ this.apiUrl }/print-jobs-behavior`, command)
            .pipe(tap(() => this.loggerService.success(true, [this.translation.instant('360SettingsSuccess')])));
    }

    public getPrintJobsAccessKeys(): Observable<ISettingsIntegrationPrintJobsAccessKeysModel> {
        return this.http.get<ISettingsIntegrationPrintJobsAccessKeysModel>(`${ this.apiUrl }/print-jobs-access-keys`);
    }

    public requestPrintJobsAccessKeys(): Observable<ISettingsIntegrationPrintJobsAccessKeysModel> {
        return this.http.get<ISettingsIntegrationPrintJobsAccessKeysModel>(`${ this.apiUrl }/print-jobs-access-keys/request`);
    }

    public revokePrintJobsAccessKeys(): Observable<boolean> {
        return this.http.get<boolean>(`${ this.apiUrl }/print-jobs-access-keys/revoke`);
    }

    public checkForIntegrationPrintJobsSettingsChanges(): Observable<ISettingsIntegrationPrintJobsBehaviorModel> {
        return this.refreshShowIntegrationPrintJobs$.asObservable();
    }

    public refreshIntegrationPrintJobsSettings(value: boolean): void {
        this.refreshShowIntegrationPrintJobs$.next({ enabled: value });
    }
}
